import React from "react";
import clsx from "clsx";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import QuickBooksJournalModal from "../clients/Tools/AFSTool/tabs/package/QuickBooksJournalModal";
import ToolsModal from "../clients/Tools/kit/ToolsModal";
import PlaybookReviewService from "../../services/PlaybookReviewService";
import BookkeepingService from "../../services/BookkeepingService";
import UtilityService from "../../services/UtilityService";

import "./agent-workflow-action.css";
import "./agent-journal-booking.css";

const NOTHING_OPEN = { isOpen: false, isChecking: false, isBooking: false, preview: null, acknowledged: {}, error: "" };
const NO_REMOVAL = { isRemoveOpen: false, isRemoving: false, removeError: "", isChangedInQuickBooks: false };

class AgentJournalBooking extends WComponent {
	constructor(props) {
		super(props);
		this.state = { ...NOTHING_OPEN, ...NO_REMOVAL, factBlockFromPreview: null };
	}

	onAskToRemove = () => {
		this.update({ ...NO_REMOVAL, isRemoveOpen: true });
	};

	onCancelRemove = () => {
		this.update({ ...NO_REMOVAL });
	};

	onRemove = async ({ removeEvenIfChanged }) => {
		const { proposal, onRemove } = this.props;
		await this.update({ isRemoving: true, removeError: "" });
		try {
			await onRemove({ proposalId: proposal.id, removeEvenIfChanged });
			await this.update({ ...NO_REMOVAL });
		} catch (error) {
			const message = UtilityService.describeRequestError({ error, fallback: "The journal was not removed. Try again." });
			const isChangedInQuickBooks = !!(error && error.response && error.response.data && error.response.data.journalChangedInQuickBooks);
			await this.update({ isRemoving: false, removeError: message, isChangedInQuickBooks });
		}
	};

	onOpen = async () => {
		const { runId, proposal } = this.props;
		await this.update({ ...NOTHING_OPEN, isOpen: true, isChecking: true });
		try {
			const preview = await PlaybookReviewService.previewJournalBooking({ runId, proposalId: proposal.id });
			const factBlockFromPreview = PlaybookReviewService.describeBookingBlock({ proposal, refusals: preview.refusals || [] });
			await this.update({ preview, isChecking: false, factBlockFromPreview });
		} catch (error) {
			const message = UtilityService.describeRequestError({ error, fallback: "QuickBooks could not be read. Try again." });
			await this.update({ error: message, isChecking: false });
		}
	};

	onClose = () => {
		this.update({ ...NOTHING_OPEN });
	};

	onAcknowledgeWarning = ({ code, checked }) => {
		this.update({ acknowledged: { ...this.state.acknowledged, [code]: checked } });
	};

	onBook = async () => {
		const { proposal, onBook } = this.props;
		const acknowledgedWarnings = BookkeepingService.listAcknowledgedWarningCodes({ acknowledged: this.state.acknowledged });
		await this.update({ isBooking: true, error: "" });
		try {
			await onBook({ proposalId: proposal.id, acknowledgedWarnings });
			await this.update({ ...NOTHING_OPEN });
		} catch (error) {
			const message = UtilityService.describeRequestError({ error, fallback: "The entry was not booked. Try again." });
			await this.update({ isBooking: false, error: message });
		}
	};

	renderOutcome({ booking }) {
		if (!booking.outcomeText) return null;
		return (
			<div className="agent-workflow-action__outcome">
				<span className={`tk-dot tk-dot--${booking.tone}`} />
				<span className="agent-workflow-action__outcome-text">{booking.outcomeText}</span>
			</div>
		);
	}

	renderModal() {
		const { proposal } = this.props;
		const { isOpen, isChecking, isBooking, preview, acknowledged, error, factBlockFromPreview } = this.state;
		if (!isOpen) return null;
		const hasPreview = !!preview;
		const refusals = hasPreview ? preview.refusals || [] : [];
		const isFactBlockNotYetWorded = !!factBlockFromPreview && !refusals.includes(factBlockFromPreview);
		const confirmation = hasPreview ? PlaybookReviewService.describeJournalBookingForConfirmation({ preview }) : { lines: [], totals: { debits: 0, credits: 0 } };
		const warnings = hasPreview ? preview.warnings : [];
		const everyWarningAcknowledged = BookkeepingService.everyWarningIsAcknowledged({ warnings, acknowledged });
		const isReadyToBook = hasPreview && preview.canBook;
		return (
			<QuickBooksJournalModal
				open
				onClose={this.onClose}
				title="Book this entry in QuickBooks"
				company={hasPreview ? preview.company.name : null}
				txnDate={hasPreview ? preview.txnDate : proposal.payload.txnDate}
				memo={hasPreview ? preview.memo : proposal.payload.memo}
				lines={confirmation.lines}
				totals={confirmation.totals}
				refusals={refusals}
				warnings={warnings}
				acknowledged={acknowledged}
				onAcknowledgeWarning={this.onAcknowledgeWarning}
				error={error}
				booking={isBooking}
				bookedAs={null}
				canBook={isReadyToBook && everyWarningAcknowledged}
				onBook={this.onBook}
				note="This writes a journal entry into the client's QuickBooks company, under a request id fixed for this proposal, so trying again never books it twice."
			>
				{isChecking && <p className="agent-journal-booking__balances">Reading QuickBooks…</p>}
				{isFactBlockNotYetWorded && (
					<p className="agent-journal-booking__balances" data-test="playbook-journal-fact-block">
						<Icon.AlertCircle size={14} />
						{factBlockFromPreview}
					</p>
				)}
				{isReadyToBook && (
					<p className="agent-journal-booking__balances">
						<Icon.Check size={14} />
						Read from QuickBooks just now: every account's balance for the month is what it was when this entry was proposed.
					</p>
				)}
			</QuickBooksJournalModal>
		);
	}

	render() {
		const { proposal, isBusy } = this.props;
		const { factBlockFromPreview } = this.state;
		const booking = PlaybookReviewService.describeJournalBooking({ proposal });
		if (booking.state === "not_accepted") return null;
		const buttonLabel = booking.state === "failed" ? "Try booking again" : "Book in QuickBooks";
		const factBlock = PlaybookReviewService.describeBookingBlock({ proposal }) || factBlockFromPreview;
		const isBlockedByFacts = booking.isBookable && !!factBlock;
		return (
			<div className={clsx("agent-workflow-action__footer", "agent-journal-booking")}>
				{this.renderOutcome({ booking })}
				{isBlockedByFacts && (
					<div className="agent-workflow-action__outcome" data-test="playbook-journal-blocked">
						<span className="tk-dot tk-dot--warning" />
						<span className="agent-workflow-action__outcome-text">{factBlock}</span>
					</div>
				)}
				{booking.isBookable && !isBlockedByFacts && (
					<div className="agent-workflow-action__buttons">
						<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={this.onOpen} disabled={isBusy}>
							{buttonLabel}
						</button>
					</div>
				)}
				{booking.isRemovable && (
					<div className="agent-workflow-action__buttons">
						<button type="button" className="tk-text-action tk-text-action--muted tk-text-action--xs" onClick={this.onAskToRemove} disabled={isBusy}>
							Remove from QuickBooks
						</button>
					</div>
				)}
				{this.renderModal()}
				{this.renderRemoveConfirmation({ booking })}
			</div>
		);
	}

	renderRemoveConfirmation({ booking }) {
		const { proposal } = this.props;
		const { isRemoveOpen, isRemoving, removeError, isChangedInQuickBooks } = this.state;
		if (!isRemoveOpen) return null;
		const journalNumber = (proposal.executionResult || {}).qboJournalEntryId;
		const footer = (
			<span className="tk-modal-actions">
				<button type="button" className="tk-button tk-button--secondary" onClick={this.onCancelRemove} disabled={isRemoving}>
					Keep it
				</button>
				{isChangedInQuickBooks ? (
					<button type="button" className="tk-button tk-button--danger" data-test="playbook-journal-remove-changed" onClick={() => this.onRemove({ removeEvenIfChanged: true })} disabled={isRemoving}>
						{isRemoving ? "Removing…" : "Delete the changed journal anyway"}
					</button>
				) : (
					<button type="button" className="tk-button tk-button--danger" data-test="playbook-journal-remove-confirm" onClick={() => this.onRemove({ removeEvenIfChanged: false })} disabled={isRemoving}>
						{isRemoving ? "Removing…" : "Remove from QuickBooks"}
					</button>
				)}
			</span>
		);
		return (
			<ToolsModal open onClose={this.onCancelRemove} title={`Remove journal ${journalNumber} from QuickBooks?`} width={520} footer={footer}>
				{removeError && (
					<div className={clsx("tk-notice", isChangedInQuickBooks ? "tk-notice--warning" : "tk-notice--danger")} data-test="playbook-journal-remove-refused">
						<p className="tk-notice__detail">{removeError}</p>
					</div>
				)}
				<p className="tk-lead">
					This deletes the journal dated {UtilityService.formatDate({ value: proposal.payload.txnDate })} from the client's QuickBooks company, and keeps a copy of what was deleted. The entry stays on
					this run as removed and cannot be booked again; run the playbook again for a fresh entry.
				</p>
				{booking.tone === "warning" && <p className="tk-lead">QuickBooks' copy did not match what was sent when it was booked, so check it in QuickBooks first.</p>}
			</ToolsModal>
		);
	}
}

export default AgentJournalBooking;
